import { useEffect, useMemo, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { IconAdjustmentsPlus } from "@tabler/icons-react";
import { classNames, debounce, useIsMounted } from "@ct-react/core";
import { ArticleType } from "@shared/url";
import { article } from "../../i18n/sharable-defs";
import { AnyArticleSearch, AnySearchKeys, useSearchContext } from "../../contexts/search";
import { formatWhere } from "../../hooks/format-gql-variables";
import { Wrapper } from "../layout/wrapper";
import Button from "../common/button";
import CheckboxInput from "../inputs/checkbox";
import SliderInput from "../inputs/slider";
import RoomsInput from "../inputs/rooms";
import "./more-filters.scss";
import { size } from "@floating-ui/react";

const transDefs = defineMessages({
  filter: { id: "search.more.trigger-label", defaultMessage: "Filtrer" },
  clean: { id: "search.more.clean", defaultMessage: "Tout effacer" },
  counted: { id: "search.more.filter-count", defaultMessage: "Afficher {count, plural, =0 {# bien} =1 {# bien} other {# biens}}"}
});

const SEARCH_PRE_FILTER_GQL_DATA = gql`
  query filterCount($where: AnyArticleFilterInput) {
    search(where: $where, first: 1) { totalCount }
  }
`;

const anyFeatures: AnySearchKeys[] = [ "cityCenter", "shoppingArea", "skiArea", "busStop", "clearView", "parking", "lift",
  "balcony", "terrace", "pool", "sauna", "fireplace", "dishwasher", "washingMachine", "tv" ];

const displayFeatures: Record<ArticleType, Array<AnySearchKeys | [ AnySearchKeys, "inverted" ]>> = {
  [ArticleType.RENTAL]: [ ...anyFeatures, "wifi", "microwave", "oven", [ "smoker", "inverted" ], "pet", "disabledPerson" ],
  [ArticleType.SEASONAL]: [ ...anyFeatures, "wifi", "pet" ],
  [ArticleType.ANNUAL]: [ ...anyFeatures, "new", "furnished" ],
  [ArticleType.SALE]: [ ...anyFeatures, "new", "furnished", "secondaryHome", "forForeigners" ]
};

type Props = {
  className?: string;
  criteria: AnyArticleSearch;
  onCriteriaChange: (criteria: AnyArticleSearch) => void;
};

const SearchedMoreFilter = ({ className, criteria, onCriteriaChange }: Props) => {

  const isMounted = useIsMounted();
  const intl = useIntl();

  const [ ctx ] = useSearchContext();

  const [ open, setOpen ] = useState<boolean>(false);
  const [ edition, setEdition ] = useState<AnyArticleSearch>(criteria);
  const [ count, setCount ] = useState<number | undefined>(undefined);

  const { data, loading } = useQuery<{ search: { totalCount: number } }>(SEARCH_PRE_FILTER_GQL_DATA, {
    ssr: false,
    variables: { where: formatWhere(edition) },
    fetchPolicy: "no-cache"
  });

  useEffect(() => {
    if (!isMounted) return;
    setEdition(criteria);
  }, [ criteria ]);

  useEffect(() => {
    !!data?.search && setCount(data.search.totalCount);
  }, [ data ]);

  const criteriaCount = useMemo(() => {
    return Object.entries(ctx[edition.kind].saving || {})
                 .filter(([ key, value ]) => typeof value !== "undefined" && key !== "kind")
                 .filter(([ key, _ ]) => !ctx[edition.kind].mainCriteria.includes(key as AnySearchKeys)).length;
  }, [ ctx[edition.kind] ]);

  const onSlide = debounce((key: AnySearchKeys, value?: any) => onChangeEditionCriteria(key, value), 500);

  const onChangeEditionCriteria = (key: AnySearchKeys, value?: any) => {
    //@ts-ignore
    const { [key]: old, ...rest } = edition;
    setEdition({
      ...rest,
      ...(typeof value !== "undefined") && { [key]: value }
    } as AnyArticleSearch);
  }

  const onClean = () => {
    const rebuild = Object.entries(edition).filter(([ key, _ ]) => ctx[edition.kind].mainCriteria.includes(key as AnySearchKeys));
    setEdition({ kind: edition.kind, ...Object.fromEntries(rebuild) } as AnyArticleSearch);
  }

  const onApply = () => {
    onCriteriaChange(edition);
    setOpen(false);
  }

  const wrapperClasses = classNames("btn outline with-icon more-filters", className, { badged: criteriaCount > 0 });

  return (
    <Wrapper.Modal {...{ open, setOpen }} size="lg">

      <Wrapper.ModalTrigger as="button" type="button" className={wrapperClasses}>
        <span className="txt">
          <IconAdjustmentsPlus className="s-icon" />
          {intl.formatMessage(transDefs.filter)}
        </span>
        {criteriaCount > 0 &&
          <span className="badge">{criteriaCount}</span>
        }
      </Wrapper.ModalTrigger>

      <Wrapper.ModalContent className="filters-group">

        {!ctx[edition.kind].mainCriteria.includes("surfaceRange") &&
          <div className="filter-group-item">
            <h2>{intl.formatMessage(article.surface)}</h2>
            {(() => {
              const surfaceRangeConfig = {
                min: 0,
                max: 250,
                step: 10,
                limitsAsUndefined: true,
                labelize: (val: number) => `${val}${val === 250 ? "+" : ""} m²`
              }
              return (
                <SliderInput config={surfaceRangeConfig}
                             value={edition.surfaceRange}
                             onChange={onSlide.bind(this, "surfaceRange")} />);
            })()}
          </div>
        }

        <div className="filter-group-item">
          <h2>{intl.formatMessage(article.rooms)}</h2>
          {!ctx[edition.kind].mainCriteria.includes("roomsRange") &&
            <>
              <h3>{intl.formatMessage(article.roomRooms)}</h3>
              <RoomsInput value={edition.rooms}
                          onChange={onChangeEditionCriteria.bind(this, "rooms")} />
            </>
          }
          <h3>{intl.formatMessage(article.bedrooms)}</h3>
          <RoomsInput max={5}
                      value={edition.bedrooms}
                      onChange={onChangeEditionCriteria.bind(this, "bedrooms")} />
          <h3>{intl.formatMessage(article.bathrooms)}</h3>
          <RoomsInput max={5}
                      value={edition.bathrooms}
                      onChange={onChangeEditionCriteria.bind(this, "bathrooms")} />
        </div>

        <div className="filter-group-item">
          <h2>{intl.formatMessage(article.equipment)}</h2>
          <div className="two-col">
            {displayFeatures[criteria.kind].map((criteria, index) => {
              const inverted = Array.isArray(criteria);
              const key = inverted ? criteria[0] : criteria;
              //@ts-ignore
              const value = inverted ? key in edition && edition[key] === false : key in edition;
              return (<div key={index} className="item">
                <CheckboxInput id={criteria.toString()}
                               label={intl.formatMessage((article as any)[key])}
                               checked={value}
                               onChange={checked => {
                                 const value = inverted ? (checked ? false : undefined) : (checked ? true : undefined);
                                 onChangeEditionCriteria(key, value);
                               }} />
              </div>);
            })}
          </div>
        </div>

      </Wrapper.ModalContent>

      <Wrapper.ModalFooter>
        <Button type="button"
                className="link"
                onClick={onClean}>
          {intl.formatMessage(transDefs.clean)}
        </Button>
        <Button type="button"
                className="outline"
                processing={loading || typeof count === "undefined"}
                onClick={onApply}>
          {intl.formatMessage(transDefs.counted, { count })}
        </Button>
      </Wrapper.ModalFooter>

    </Wrapper.Modal>);
};

export default SearchedMoreFilter;
